import { useState, useEffect, useMemo, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  useGetAiChatConversationsLazyQuery,
  useGetAiChatMessagesLazyQuery,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "./use-workspace-id";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { isLovablePreview } from "@/lib/utils/environment";
import { displayLocale } from "@/lib/i18n/display";

const HISTORY_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 300;
const ENTITY_SEARCH_SUFFIX = "ai-chat-entity-search-history";
const MAX_ENTITY_SEARCHES = 50;

/** Parse a DateTime scalar that may be an ISO string, epoch seconds, or epoch milliseconds. */
function parseTimestamp(ts: unknown): Date {
  if (!ts) return new Date(0);
  if (typeof ts === "number") {
    // Epoch seconds (10 digits) vs milliseconds (13 digits)
    return new Date(ts < 1e12 ? ts * 1000 : ts);
  }
  // Backend returns UTC timestamps without Z suffix — append it so JS doesn't treat as local time
  let str = ts as string;
  if (typeof str === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) && !/[Zz]$|[+-]\d{2}(:\d{2})?$/.test(str)) {
    str += "Z";
  }
  const d = new Date(str);
  if (isNaN(d.getTime())) {
    console.warn("[use-ai-chat-history] Unparseable timestamp:", ts);
    return new Date(0);
  }
  return d;
}

export interface AiChatConversationItem {
  id: number;
  agentSessionId: string;
  contextType: string | null;
  firstMessage: string | null;
  relativeTime: string;
  updateTs: string;
}

export interface EntitySearchEntry {
  id: string;
  entityType: "person" | "company" | "deal" | "meeting";
  entityName: string;
  timestamp: string; // ISO 8601
}

export function appendEntitySearch(
  prev: EntitySearchEntry[],
  entry: Omit<EntitySearchEntry, "id" | "timestamp">,
): EntitySearchEntry[] {
  const newEntry: EntitySearchEntry = {
    id: `entity-${Date.now()}`,
    ...entry,
    timestamp: new Date().toISOString(),
  };
  // Dedupe: remove previous search for same entity name+type
  const filtered = prev.filter(
    (s) => !(s.entityName === entry.entityName && s.entityType === entry.entityType)
  );
  return [newEntry, ...filtered].slice(0, MAX_ENTITY_SEARCHES);
}

export interface AiChatMessageItem {
  id: number;
  role: "user" | "assistant";
  content: string;
  createTs: string;
  /** Existing feedback vote on an assistant message: 1 = up, -1 = down. */
  vote?: number | null;
}

// Dummy search history for preview mode (previously available before backend integration)
const DUMMY_SEARCH_HISTORY: AiChatConversationItem[] = [
  { id: 1, agentSessionId: "demo-1", contextType: "general", firstMessage: "Which deals need attention?", relativeTime: "2 hours ago", updateTs: new Date(Date.now() - 2 * 3600 * 1000).toISOString() },
  { id: 2, agentSessionId: "demo-2", contextType: "general", firstMessage: "Summarize my call with Sarah Chen", relativeTime: "Yesterday", updateTs: new Date(Date.now() - 86400 * 1000).toISOString() },
  { id: 3, agentSessionId: "demo-3", contextType: "general", firstMessage: "Who should I follow up with this week?", relativeTime: "Yesterday", updateTs: new Date(Date.now() - 86400 * 1000 - 3600 * 1000).toISOString() },
  { id: 4, agentSessionId: "demo-4", contextType: "general", firstMessage: "What are the key takeaways from my recent meetings?", relativeTime: "2 days ago", updateTs: new Date(Date.now() - 2 * 86400 * 1000).toISOString() },
  { id: 5, agentSessionId: "demo-5", contextType: "general", firstMessage: "What's our weighted forecast for Q1?", relativeTime: "3 days ago", updateTs: new Date(Date.now() - 3 * 86400 * 1000).toISOString() },
  { id: 6, agentSessionId: "demo-6", contextType: "general", firstMessage: "What are my open action items from this week?", relativeTime: "4 days ago", updateTs: new Date(Date.now() - 4 * 86400 * 1000).toISOString() },
];

export function useAiChatHistory({ enabled = true }: { enabled?: boolean } = {}) {
  const workspaceId = useWorkspaceId();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [fetchConversations, { data, loading }] =
    useGetAiChatConversationsLazyQuery({ fetchPolicy: "network-only" });

  const [messagesError, setMessagesError] = useState<Error | null>(null);
  const [fetchMessages, { data: messagesData, loading: messagesLoading }] =
    useGetAiChatMessagesLazyQuery({
      fetchPolicy: "network-only",
      onError: (err) => setMessagesError(err),
      onCompleted: () => setMessagesError(null),
    });

  // Debounce the search value
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Re-fetch whenever the debounced search changes (only when workspace is known and not preview).
  // Gated on `enabled` so the hook does not auto-fetch when its host panel is closed —
  // see docs/COMMON_BUGS.md "Hooks Auto-Fetching in Always-Mounted Components".
  useEffect(() => {
    if (!enabled || !workspaceId || isLovablePreview()) return;
    fetchConversations({
      variables: {
        workspaceId,
        search: debouncedSearch || undefined,
        limit: HISTORY_LIMIT,
      },
    });
  }, [enabled, debouncedSearch, workspaceId, fetchConversations]);

  // Preview mode: filter dummy data locally
  const previewConversations = useMemo(() => {
    if (!isLovablePreview()) return [];
    if (!debouncedSearch.trim()) return DUMMY_SEARCH_HISTORY;
    const lower = debouncedSearch.toLowerCase();
    return DUMMY_SEARCH_HISTORY.filter(
      (item) => item.firstMessage?.toLowerCase().includes(lower)
    );
  }, [debouncedSearch]);

  // Entity search history — workspace-scoped so workspace A's searched
  // people/companies don't leak into workspace B's Ask AI history.
  const [entitySearches, setEntitySearches] = useWorkspaceLocalStorage<EntitySearchEntry[]>(
    ENTITY_SEARCH_SUFFIX,
    [],
  );

  const recordEntitySearch = useCallback((entityType: EntitySearchEntry["entityType"], entityName: string) => {
    setEntitySearches(prev => appendEntitySearch(prev, { entityType, entityName }));
  }, [setEntitySearches]);

  const backendConversations: AiChatConversationItem[] = isLovablePreview()
    ? previewConversations
    : (data?.aiChatConversations ?? [])
        .filter(Boolean)
        .map((c) => ({
          id: c!.id!,
          agentSessionId: c!.agentSessionId ?? "",
          contextType: c!.contextType ?? null,
          firstMessage: c!.firstMessage ?? null,
          relativeTime: c!.updateTs
            ? formatDistanceToNow(parseTimestamp(c!.updateTs), { addSuffix: true, locale: displayLocale() })
            : "",
          updateTs: c!.updateTs ? parseTimestamp(c!.updateTs).toISOString() : "",
        }));

  // Merge backend conversations with entity searches, sorted by time (newest first)
  const conversations = useMemo(() => {
    const entityItems: (AiChatConversationItem & { historyType: "search"; searchType: string; searchEntity: string })[] =
      entitySearches
        .filter((e) => {
          if (!debouncedSearch.trim()) return true;
          return e.entityName.toLowerCase().includes(debouncedSearch.toLowerCase());
        })
        .map((e) => ({
          id: -parseInt(e.id.split("-")[1] || "0", 10),
          agentSessionId: "",
          contextType: null,
          firstMessage: e.entityName,
          relativeTime: formatDistanceToNow(new Date(e.timestamp), { addSuffix: true, locale: displayLocale() }),
          updateTs: e.timestamp,
          historyType: "search" as const,
          searchType: e.entityType,
          searchEntity: e.entityName,
        }));

    const promptItems = backendConversations.map((c) => ({
      ...c,
      historyType: "prompt" as const,
      searchType: undefined as string | undefined,
      searchEntity: undefined as string | undefined,
    }));

    return [...entityItems, ...promptItems].sort(
      (a, b) => new Date(b.updateTs).getTime() - new Date(a.updateTs).getTime()
    );
  }, [backendConversations, entitySearches, debouncedSearch]);

  const messages: AiChatMessageItem[] = (messagesData?.aiChatMessages ?? [])
    .filter(Boolean)
    .map((m) => ({
      id: m!.id!,
      role: (m!.role ?? "user") as "user" | "assistant",
      content: m!.content ?? "",
      createTs: m!.createTs ?? "",
      vote: m!.vote ?? null,
    }));

  const openHistory = () => {
    if (isLovablePreview()) return;
    if (!workspaceId) return;
    fetchConversations({
      variables: { workspaceId, search: debouncedSearch || undefined, limit: HISTORY_LIMIT },
    });
  };

  const loadMessages = (conversationId: number) => {
    setMessagesError(null);
    fetchMessages({ variables: { conversationId } });
  };

  return {
    search,
    setSearch,
    conversations,
    loading: isLovablePreview() ? false : loading,
    openHistory,
    loadMessages,
    messages,
    messagesLoading,
    messagesError,
    recordEntitySearch,
  };
}
